import AuthDataProvider from '../components/auth/AuthDataProvider';
import AtlasDataProvider from '../components/atlas/AtlasDataProvider';
import { buildAtlasReferralUrl } from '../components/atlas/atlasHelpers';
import ThemeManager from '../core/ThemeManager';
import {
	setUrlLoadStatus,
	setReferralUrl,
	setUrlLoadError,
	setStatusLoadStatus,
	setAtlasAliveStatus,
	clearReferralUrlCache
} from './atlasSlice';

let inFlightReferralUrl = null;
let inFlightReferralGeneration = null;
let inFlightAliveStatus = null;
let inFlightAliveGeneration = null;

const isCurrentRequest = (getState, requestGeneration) =>
	getState().atlas.requestGeneration === requestGeneration;

export const ensureReferralUrl = () => async (dispatch, getState) => {
	const { referralUrl, mode, urlLoadStatus, requestGeneration } = getState().atlas;
	const currentMode = ThemeManager.getCurrentTheme();
	if (referralUrl && mode === currentMode) {
		return referralUrl;
	}
	if (referralUrl && mode !== currentMode) {
		dispatch(clearReferralUrlCache());
	}
	if (
		urlLoadStatus === 'loading'
		&& inFlightReferralUrl
		&& inFlightReferralGeneration === requestGeneration
	) {
		return inFlightReferralUrl;
	}

	const requestGenerationSnapshot = requestGeneration;
	dispatch(setUrlLoadStatus('loading'));
	dispatch(setUrlLoadError(null));

	const promise = AuthDataProvider.fetchReferralToken('data_explorer')
		.then((data) => {
			if (!isCurrentRequest(getState, requestGenerationSnapshot)) {
				return null;
			}
			const rawUrl = data.referral_url || data.referralUrl;
			const mode = ThemeManager.getCurrentTheme();
			const builtUrl = buildAtlasReferralUrl(rawUrl, mode);
			dispatch(setReferralUrl({ referralUrl: builtUrl, mode }));
			dispatch(setUrlLoadStatus('ready'));
			return builtUrl;
		})
		.catch((err) => {
			if (isCurrentRequest(getState, requestGenerationSnapshot)) {
				dispatch(setUrlLoadError(err?.message || 'Error loading Atlas'));
			}
			throw err;
		})
		.finally(() => {
			if (
				inFlightReferralUrl === promise
				&& inFlightReferralGeneration === requestGenerationSnapshot
			) {
				inFlightReferralUrl = null;
				inFlightReferralGeneration = null;
			}
		});

	inFlightReferralUrl = promise;
	inFlightReferralGeneration = requestGenerationSnapshot;
	return promise;
};

export const refreshAtlasAliveStatus = () => async (dispatch, getState) => {
	const { requestGeneration } = getState().atlas;
	if (inFlightAliveStatus && inFlightAliveGeneration === requestGeneration) {
		return inFlightAliveStatus;
	}

	const requestGenerationSnapshot = requestGeneration;
	const runCheck = async () => {
		let referralUrl = getState().atlas.referralUrl;
		if (!referralUrl) {
			referralUrl = await dispatch(ensureReferralUrl());
		}
		if (!referralUrl) {
			if (isCurrentRequest(getState, requestGenerationSnapshot)) {
				dispatch(setAtlasAliveStatus('down'));
			}
			return false;
		}

		if (!isCurrentRequest(getState, requestGenerationSnapshot)) {
			return false;
		}
		dispatch(setStatusLoadStatus('loading'));
		try {
			await AtlasDataProvider.getStatus(referralUrl);
			if (isCurrentRequest(getState, requestGenerationSnapshot)) {
				dispatch(setAtlasAliveStatus(true));
			}
			return true;
		} catch {
			if (isCurrentRequest(getState, requestGenerationSnapshot)) {
				dispatch(setAtlasAliveStatus('down'));
			}
			return false;
		}
	};

	const promise = runCheck().finally(() => {
		if (
			inFlightAliveStatus === promise
			&& inFlightAliveGeneration === requestGenerationSnapshot
		) {
			inFlightAliveStatus = null;
			inFlightAliveGeneration = null;
		}
	});
	inFlightAliveStatus = promise;
	inFlightAliveGeneration = requestGenerationSnapshot;
	return promise;
};

export const ensureAtlasEmbed = ({ refreshStatus = false } = {}) => async (dispatch, getState) => {
	await dispatch(ensureReferralUrl());
	const { statusLoadStatus } = getState().atlas;
	if (refreshStatus || statusLoadStatus === 'idle') {
		await dispatch(refreshAtlasAliveStatus());
	}
};
